<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2019-2023 (original work) Open Assessment Technologies SA ;
    import { __, generateElementId, mutationObserver as useMutationObserver } from '@oat-sa-private/ui-core';
    import { afterUpdate, onMount } from 'svelte';

    export let label = '';
    export let hiddenLabel = __('Input');
    export let fullwidth = false;
    export let required = false;
    export let forAttr;
    export let id = generateElementId('label');

    let contentEl = null;
    let inputAttributesUpdateNeeded = false;
    let firstInputMutationObserver;

    $: contentEl && inputAttributesUpdateNeeded && applyInputAttributes();

    function applyInputAttributes() {
        inputAttributesUpdateNeeded = false;
        const inputs = contentEl.querySelectorAll('input');
        if (!inputs.length) {
            return;
        }

        if (!forAttr) {
            if (inputs[0].hasAttribute('id')) {
                forAttr = inputs[0].getAttribute('id');
            } else {
                const inputId = generateElementId('input');
                inputs[0].setAttribute('id', inputId);
                forAttr = inputId;
            }
        }

        if (firstInputMutationObserver) {
            firstInputMutationObserver.destroy();
        }
        firstInputMutationObserver = useMutationObserver(inputs[0], {
            callback: () => {
                forAttr = inputs[0].getAttribute('id');
            },
            observeOptions: {
                attributes: true,
                attributeFilter: ['id']
            }
        });

        for (let input of inputs) {
            if (!input.hasAttribute('aria-labelledby')) {
                input.setAttribute('aria-labelledby', id);
            }
        }
    }

    onMount(() => {
        inputAttributesUpdateNeeded = true;
    });

    afterUpdate(() => {
        inputAttributesUpdateNeeded = true;
    });
</script>

<style>
    .label-root {
        display: inline-flex;
        flex-direction: column;
        align-items: flex-start;

        &.fullwidth {
            display: block;
        }

        & .label-text {
            margin-block: 0 var(--space-1x);
            margin-inline: 0 var(--space-1x);
            font-size: var(--fontsize-heading);
            @add-mixin bold;

            & [title='required'] {
                color: var(--color-alert);
                padding: 0 var(--space-half);
            }
        }
    }
</style>

<!-- svelte-ignore a11y-label-has-associated-control -->
<div class="label-root" class:fullwidth>
    <label class="label-text" for={forAttr} {id} class:visually-hidden={!label}>
        {label || hiddenLabel}
        {#if label && required}
            <abbr title="required" aria-hidden="true">*</abbr>
        {/if}
    </label>
    <div class="label-content" bind:this={contentEl}>
        <slot />
    </div>
</div>
